// LO STRATO DEL TAVOLO GUIDATO (I-1218; disegno del KB `reports/2026-10-26-tavolo-guidato-carte-disegno.md` §3 e §5):
// copiato da src/components/solitario/TutorialSolitario.tsx:228-538 @ cad51507af021388ceb3a5de34eb61259665077d (il livello, la testata con la barra a segmenti, l'uscita, il fuoco,
// l'apertura e il riepilogo), generalizzato: il tavolo, le regole, i bersagli e i testi sono del gioco. NON si copia il «lampo» (condizione di tma-orcb, 22:42:54Z): nessun passo avanza da solo, la scheda guida e` quella della rev.1 (`SchedaGuida.tsx`).
import { useEffect, useId, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { SopraLaBarra } from '@/components/common/SopraLaBarra';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { cn } from '@/lib/cn';
import { aspettaMossa, avantiInVista, creaEsecutore, inLettura, puoAvanzare, puoMostrare } from '@/lib/tutorial/esecutore';
import { PASSO_DOPO_MS } from '@/lib/tutorial/ritmo';
import type { Copione, Motore, MossaDiGioco } from '@/lib/tutorial/tipi';

import { Accensioni } from './Accensioni';
import { Scheda } from './Scheda';
import { SchedaGuida } from './SchedaGuida';
import type { AzioneScheda, FormatoScheda, VoceStato } from './SchedaGuida';

export type { FormatoScheda } from './SchedaGuida';

/** I testi dello strato, nella lingua dell'app: li da` il gioco (la zona non ha un suo namespace e non scrive testi). */
export interface TestiStrato {
  /** «Come si gioca». */
  titolo: string;
  /** Il sottotitolo nei passi, «Passo n di tot». */
  passo: (n: number, tot: number) => string;
  /** Il sottotitolo all'apertura e al riepilogo. */
  apertura: string;
  riepilogo: string;
  esci: string;
  avanti: string;
  mostramelo: string;
  /** Il riquadro di stato dopo una mossa sbagliata. */
  nonLi: string;
  /** Il riquadro di stato dopo una mossa `altra`, se il passo non ha un suo perche`. */
  altra: string;
  giusto: string;
  /** La riga d'azione muta, in attesa della mossa: «Fai la mossa per andare avanti». */
  faiLaMossa: string;
  inizia: string;
  nonOra: string;
  rivedi: string;
  capito: string;
  /**
   * Il riquadro di stato MENTRE la coda di `dopo` si mostra (le mosse degli altri posti: «Avanti» e` disabilitato fino alla fine), per esempio «Tocca all'avversario…». Sostituisce il «Giusto!» finche` la coda non e`
   * vuota (il gioco puo` cominciare con «Giusto!»); senza, resta il «Giusto!». Nei passi di LETTURA in compatta (o `auto` sotto i 720 px) lo stesso testo sta anche nel bottone «Avanti», spento, al posto di «Avanti →»,
   * con l'icona della clessidra: deve stare INTERO su una riga a 360×640 (la zona non lo tronca ne` lo manda a capo: un testo che non ci sta e` un rosso del banco del gioco, e il gioco lo accorcia). Lo scrive il gioco: la zona non ha testi suoi (richiesta di briscola-orcb, 27/10; tma-ui-orcb 00:57Z: «il testo "aspetta" va nella seconda PR»).
   */
  turnoAltrui?: string;
}

/** I testi di un passo (per convenzione `<gioco>:tutorial.passi.<id>.*`). `come` sta nel riquadro di stato in attesa; `altra` e` il perche` del passo per una mossa legale ma diversa. */
export interface TestiPasso {
  titolo: string;
  testo: string;
  come?: string;
  altra?: string;
  /**
   * Il PERCHE` di una mossa SBAGLIATA (illegale) in QUESTO passo (richiesta di tressette-orcb; per convenzione `<gioco>:…passi.<id>.sbagliata`): sostituisce il «Non lì» del riquadro, con lo stesso tono.
   * Se manca, o e` vuoto, vale `TestiStrato.nonLi` come sempre. Una mossa `altra` (legale) non lo legge mai.
   */
  sbagliata?: string;
  /** Il riquadro di stato dopo la giusta in QUESTO passo (richiesta di settemezzo-orcb, 27/10): se manca vale `TestiStrato.giusto`. */
  giusto?: string;
}

/**
 * Una scheda d'apertura o di fine. `azioni` (decisione di tma-orcb, 23:10:24Z, sulle richieste del burraco): bottoni in piu`, con la chiave i18n del gioco come testo; la zona non sa cosa
 * fa `onPress` (per esempio «Gioca contro il Principiante»). Senza `azioni` la scheda non cambia.
 */
export interface SchedaStrato {
  titolo: string;
  corpo: ReactNode;
  azioni?: readonly { chiave: string; onPress: () => void }[];
}

/** Cio` che lo strato passa al tavolo vero del gioco. */
export interface PropsTavolo<V, M, B> {
  vista: V;
  /**
   * Il tavolo chiama `onMossa` con una mossa INTERA: lo strato giudica solo allora. Una mossa composta (cinque piazzamenti e «Conferma» nel Poker Cinese, il segno e poi la carta nel tressette,
   * la carta e le prese nella scopa) la raccoglie il tavolo nei suoi tempi; lo strato non presuppone una mossa da un solo tocco.
   */
  onMossa: (m: M) => void;
  /** Il tavolo accetta mosse solo mentre il passo ne aspetta una. */
  abilitato: boolean;
  /** I bersagli che il passo illumina (lo strato li illumina gia`: il tavolo puo` usarli, per esempio, per alzare una carta). */
  evidenze: readonly B[];
}

export interface PropsStrato<S, V, M extends MossaDiGioco, B> {
  /** `copione` e `motore` vanno dichiarati a livello di modulo (o in un `useMemo`): se cambiano a ogni render `statiDiPartenza` si ricalcola e l'esecutore si rifa. */
  copione: Copione<S, M, B>;
  motore: Motore<S, V, M>;
  /**
   * Disegna il tavolo VERO del gioco. Lo strato lo tiene montato per tutto il tutorial e non lo rimonta fra un giudizio e l'altro: una selezione a due tocchi del tavolo resta. Passa un
   * componente stabile (una funzione a livello di modulo), non uno scritto in linea.
   */
  rendi: (p: PropsTavolo<V, M, B>) => ReactNode;
  /** Trova un bersaglio nel tavolo (dai suoi attributi `data-*`): un Element qualunque, anche un nodo SVG; `null` se non c'e` (niente accensione, mai un errore). */
  bersaglio: (b: B, tavolo: HTMLElement) => Element | null;
  testi: TestiStrato;
  testoPasso: (id: string) => TestiPasso;
  /** Il testo di una CHIAVE i18n del gioco: il perche` dato da `Passo.altra(m)`, le etichette di `azioni`. Di solito `(k) => t(k)`. */
  testoChiave: (chiave: string) => string;
  apertura: SchedaStrato;
  riepilogo: SchedaStrato;
  /** Cosa mettere dentro il fantasma di «Mostramelo» (per esempio la carta): senza, e` un riquadro grande quanto l'origine. */
  rendiFantasma?: (b: B) => ReactNode;
  /**
   * Il formato della scheda guida: `'auto'` (default) e` la piena e diventa compatta da sola sotto i 720 px di altezza (`@media(max-height:720px)`, come #861); `'piena'` e `'compatta'` lo forzano
   * qualunque sia l'altezza. Senza `scheda` si sceglie `auto`: a 390×844 e` la piena.
   */
  scheda?: FormatoScheda;
  onChiudi: () => void;
}

/** Il vuoto fra la testata, il tavolo e la scheda: 6 px, 4 nella compatta (come #861). Scritte per intero: Tailwind le legge dal sorgente. */
const GAP_STRATO: Record<FormatoScheda, string> = { piena: 'gap-1.5', compatta: 'gap-1', auto: 'gap-1.5 [@media(max-height:720px)]:gap-1' };

/**
 * Il TUTORIAL di un gioco: un LIVELLO a tutto schermo a `z-[60]`, sopra la barra, non una rotta. E` lo STESSO tavolo del gioco con lo stato del copione, calcolato dalle regole del gioco.
 * ZERO rete, niente che conti, nessuno storage (il «gia` visto» e` di `IconaTutorial`). Si monta solo dalla Hub del gioco, mai sopra una partita viva (`tools/verify-tutorial.ts`).
 * Esci (la X), il tasto indietro di Telegram ed Escape chiudono subito; non c'e` ripresa. Il fuoco va su «Inizia» all'apertura, sul livello nei passi, su «Ho capito» alla fine.
 */
export function StratoTutorial<S, V, M extends MossaDiGioco, B>({
  copione,
  motore,
  rendi,
  bersaglio,
  testi,
  testoPasso,
  testoChiave,
  apertura,
  riepilogo,
  rendiFantasma,
  scheda: formatoScheda = 'auto',
  onChiudi,
}: PropsStrato<S, V, M, B>) {
  const idTitolo = useId();
  const radice = useRef<HTMLDivElement | null>(null);
  const zonaTavolo = useRef<HTMLDivElement | null>(null);
  const tavolo = useRef<HTMLDivElement | null>(null);
  const esecutore = useMemo(() => creaEsecutore(motore, copione), [motore, copione]);
  const [st, invia] = useReducer(esecutore.riduci, undefined, esecutore.iniziale);

  useLivelloIndietro(true, onChiudi);
  useEffect(() => {
    const suTasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi();
    };
    window.addEventListener('keydown', suTasto);
    return () => window.removeEventListener('keydown', suTasto);
  }, [onChiudi]);

  // il fuoco resta dentro il livello: su «Inizia» all'apertura, sul livello nei passi, su «Ho capito» alla fine; dentro un passo non si sposta mai da solo
  useEffect(() => {
    const el = radice.current;
    if (el === null) return;
    if (st.fase === 'apertura') el.querySelector<HTMLElement>('[data-azione="inizia"]')?.focus();
    else if (st.fase === 'riepilogo') el.querySelector<HTMLElement>('[data-azione="capito"]')?.focus();
    else el.focus();
  }, [st.fase]);

  // l'elemento che tiene il fuoco ESCE dal DOM senza che cambi il passo (la carta giocata, il «Passa» del tavolo: la mossa lo smonta): il fuoco cadrebbe su `document.body`, fuori dal dialog `aria-modal`, e
  // nessun effetto sopra rigira perche` ne` l'indice ne` la fase cambiano. Un osservatore sulla radice lo vede: se dopo una modifica il fuoco e` su `body` (o su niente) torna dove sta per la fase; se e` su
  // QUALUNQUE altro elemento (dentro lo strato, o un dialogo del consumatore sopra lo strato) non si tocca. Si scollega alla chiusura: a livello smontato il fuoco e` del consumatore.
  const faseCorrente = useRef(st.fase);
  useLayoutEffect(() => {
    faseCorrente.current = st.fase;
  }, [st.fase]);
  useEffect(() => {
    const el = radice.current;
    if (el === null) return;
    const osservatore = new MutationObserver(() => {
      const fuoco = document.activeElement;
      if (fuoco !== null && fuoco !== document.body) return;
      const fase = faseCorrente.current;
      if (fase === 'apertura') el.querySelector<HTMLElement>('[data-azione="inizia"]')?.focus({ preventScroll: true });
      else if (fase === 'riepilogo') el.querySelector<HTMLElement>('[data-azione="capito"]')?.focus({ preventScroll: true });
      else el.focus({ preventScroll: true });
    });
    osservatore.observe(el, { childList: true, subtree: true });
    return () => osservatore.disconnect();
  }, []);

  // «Avanti» lascia il posto alla riga muta quando il passo dopo aspetta una mossa: React smonta il bottone che aveva il fuoco e il fuoco cadrebbe su `document.body`, fuori dal dialog. Se non e` piu`
  // dentro il livello lo si rimette sul livello; se e` gia` dentro non si tocca (dentro un passo il fuoco non si sposta mai da solo)
  useEffect(() => {
    const el = radice.current;
    if (el !== null && st.fase === 'passi' && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [st.indice, st.fase]);

  // le mosse di `dopo`, una per volta a passo leggibile; il passo NON avanza: si va avanti solo col tocco su «Avanti»
  useEffect(() => {
    if (st.coda.length === 0) return;
    const timer = setTimeout(() => invia({ tipo: 'dopo' }), PASSO_DOPO_MS);
    return () => clearTimeout(timer);
  }, [st.coda]);

  const passi = copione.passi;
  const n = passi.length;
  const passo = passi[st.indice];
  const inGioco = aspettaMossa(st, passo);
  const leggendo = inLettura(st, passo);
  const testiPassi = useMemo(() => passi.map((p) => ({ id: p.id, ...testoPasso(p.id) })), [passi, testoPasso]);
  const voci = useMemo<VoceStato[]>(
    () => [
      { chiave: 'nonLi', riscontro: 'nonLi', testo: testi.nonLi },
      { chiave: 'giusto', riscontro: 'giusto', testo: testi.giusto },
      ...(testi.turnoAltrui !== undefined ? [{ chiave: 'turnoAltrui', riscontro: 'altrui' as const, testo: testi.turnoAltrui }] : []),
      ...testiPassi.flatMap((p): VoceStato[] => [
        { chiave: `come:${p.id}`, riscontro: 'attesa', testo: p.come ?? '' },
        { chiave: `altra:${p.id}`, riscontro: 'altra', testo: p.altra ?? testi.altra },
        ...(p.sbagliata !== undefined && p.sbagliata.trim() !== '' ? [{ chiave: `sbagliata:${p.id}`, riscontro: 'nonLi' as const, testo: p.sbagliata }] : []),
        ...(p.giusto !== undefined ? [{ chiave: `giusto:${p.id}`, riscontro: 'giusto' as const, testo: p.giusto }] : []),
      ]),
      // i perche` DICHIARATI dai passi (`Passo.perche`), una volta per chiave: la cella piena li riserva come i testi statici
      ...[...new Set(passi.flatMap((p) => p.perche ?? []))].map((k): VoceStato => ({ chiave: `perche:${k}`, riscontro: 'altra', testo: testoChiave(k) })),
    ],
    [testiPassi, passi, testoChiave, testi.nonLi, testi.giusto, testi.altra, testi.turnoAltrui],
  );
  const id = passo?.id ?? '';
  const perche = st.riscontro === 'altra' && st.perche !== undefined ? testoChiave(st.perche) : undefined;
  // un perche` dichiarato e` gia` nella cella; uno non dichiarato si aggiunge solo mentre si mostra (e la cella piena puo` crescere)
  const percheRiservato = perche !== undefined && voci.some((v) => v.chiave === `perche:${st.perche}`);
  const vociInVista = perche === undefined || percheRiservato ? voci : [...voci, { chiave: 'perche', riscontro: 'altra' as const, testo: perche }];
  // mentre la coda di `dopo` si mostra (dopo la giusta, o dopo il primo tocco su «Avanti» di una lettura) e il gioco ha dato il suo testo, il riquadro dice che tocca agli altri; poi torna il «Giusto!» (del passo, se ne ha uno)
  const turnoAltrui = st.coda.length > 0 && testi.turnoAltrui !== undefined;
  const giustoDelPasso = st.riscontro === 'giusto' && testiPassi[st.indice]?.giusto !== undefined;
  const attiva = turnoAltrui ? 'turnoAltrui' : perche !== undefined ? (percheRiservato ? `perche:${st.perche}` : 'perche') : st.riscontro === 'attesa' ? `come:${id}` : st.riscontro === 'altra' ? `altra:${id}` : st.riscontro === 'nonLi' && voci.some((v) => v.chiave === `sbagliata:${id}`) ? `sbagliata:${id}` : giustoDelPasso ? `giusto:${id}` : st.riscontro;
  const azione: AzioneScheda = avantiInVista(st, passo) ? 'avanti' : puoMostrare(st, passo) ? 'mostramelo' : 'attesa';

  const numero = st.fase === 'passi' ? st.indice + 1 : st.fase === 'riepilogo' ? n : 0;
  const sottotitolo = st.fase === 'passi' ? testi.passo(numero, n) : st.fase === 'riepilogo' ? testi.riepilogo : testi.apertura;
  const scheda = st.fase === 'apertura' ? apertura : st.fase === 'riepilogo' ? riepilogo : null;

  return (
    <SopraLaBarra>
      <div
        ref={radice}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitolo}
        tabIndex={-1}
        data-strato-tutorial
        data-fase={st.fase}
        data-passo={st.fase === 'passi' ? st.indice + 1 : undefined}
        className={cn('fixed inset-0 z-[60] flex flex-col bg-bg px-2 outline-none', GAP_STRATO[formatoScheda])}
        style={{
          paddingTop: 'max(12px, var(--tg-viewport-safe-area-inset-top, 0px) + var(--tg-viewport-content-safe-area-inset-top, 0px))',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px), var(--tg-viewport-safe-area-inset-bottom, 0px))',
        }}
      >
        {/* la testata: «Come si gioca · Passo n di tot», la barra a segmenti, «Esci» */}
        <div className="flex shrink-0 items-center gap-2 px-1">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-col">
              <p data-titolo-livello className="text-base font-extrabold leading-5 text-fg">
                {testi.titolo}
              </p>
              <p data-sottotitolo-livello className="text-xs text-fg-muted">
                {sottotitolo}
              </p>
            </div>
            <div
              {...(numero > 0 ? { role: 'progressbar', 'aria-valuemin': 1, 'aria-valuemax': n, 'aria-valuenow': numero, 'aria-label': sottotitolo } : { 'aria-hidden': true })}
              className="flex gap-1"
            >
              {Array.from({ length: n }, (_, i) => {
                // i passi fatti pieni, quello in corso a meta`, gli altri vuoti; al riepilogo sono tutti fatti
                const stato = st.fase === 'riepilogo' || (st.fase === 'passi' && i < st.indice) ? 'pieno' : st.fase === 'passi' && i === st.indice ? 'meta' : 'vuoto';
                return <span key={i} data-segmento={stato} className={cn('h-1 flex-1 rounded-full', stato === 'pieno' ? 'bg-brand' : stato === 'meta' ? 'bg-[linear-gradient(90deg,var(--color-brand)_50%,var(--color-bg-tertiary)_50%)]' : 'bg-bg-tertiary')} />;
              })}
            </div>
          </div>
          <Button variant="icona" size={40} tono="pieno" data-azione="esci" aria-label={testi.esci} onClick={onChiudi}>
            <X size={20} aria-hidden="true" />
          </Button>
        </div>

        {/* il tavolo vero, SEMPRE montato nello stesso posto (apertura, passi, riepilogo): le accensioni, il fantasma e le schede stanno sopra. `isolate`: gli z-index del tavolo del gioco (la mano a ventaglio arriva a z 12) restano DENTRO il contenitore e non superano mai il velo (z-10) e le accensioni, suoi fratelli */}
        <div ref={zonaTavolo} className="relative flex min-h-0 flex-1 flex-col">
          <div ref={tavolo} data-tavolo-tutorial className="isolate flex min-h-0 flex-1 flex-col">
            {rendi({ vista: motore.vista(st.stato), onMossa: (m) => invia({ tipo: 'mossa', mossa: m }), abilitato: inGioco, evidenze: (inGioco || leggendo) && passo !== undefined ? passo.evidenzia : [] })}
          </div>
          <Accensioni
            radice={zonaTavolo}
            tavolo={tavolo}
            bersaglio={bersaglio}
            evidenze={passo?.evidenzia ?? []}
            accese={inGioco || leggendo}
            daGuardare={leggendo}
            mostra={passo?.mostra}
            fantasma={st.mostra && inGioco}
            rendiFantasma={rendiFantasma}
          />
          {scheda !== null && (
            <div data-scheda-tutorial={st.fase} className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-bg/85 p-1">
              <Scheda
                className="shadow-[0_20px_40px_rgb(0_0_0/0.5)]"
                piede={
                  st.fase === 'apertura' ? (
                    <>
                      <Button variant="neutro" fullWidth className="text-fg" data-azione="non-ora" onClick={onChiudi}>
                        {testi.nonOra}
                      </Button>
                      <Button variant="primary" fullWidth data-azione="inizia" onClick={() => invia({ tipo: 'inizia' })}>
                        {testi.inizia}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="neutro" fullWidth className="text-fg" data-azione="rivedi" onClick={() => invia({ tipo: 'rivedi' })}>
                        {testi.rivedi}
                      </Button>
                      <Button variant="primary" fullWidth data-azione="capito" onClick={onChiudi}>
                        {testi.capito}
                      </Button>
                    </>
                  )
                }
              >
                <h2 id={idTitolo} data-titolo-scheda className="font-display text-xl font-bold leading-tight tracking-tight text-fg">
                  {scheda.titolo}
                </h2>
                {scheda.corpo}
                {scheda.azioni !== undefined && scheda.azioni.length > 0 && (
                  <div data-azioni-scheda className="flex flex-col gap-2">
                    {scheda.azioni.map((a) => (
                      <Button key={a.chiave} variant="secondary" fullWidth data-azione-scheda={a.chiave} onClick={a.onPress}>
                        {testoChiave(a.chiave)}
                      </Button>
                    ))}
                  </div>
                )}
              </Scheda>
            </div>
          )}
        </div>

        {st.fase === 'passi' && (
          <SchedaGuida
            idTitolo={idTitolo}
            passi={testiPassi}
            indice={st.indice}
            voci={vociInVista}
            attiva={attiva}
            riscontro={turnoAltrui ? 'altrui' : st.riscontro}
            azione={azione}
            avantiAttivo={puoAvanzare(st, passo)}
            mostra={st.mostra}
            lettura={leggendo}
            formato={formatoScheda}
            onAvanti={() => invia({ tipo: 'avanti' })}
            onMostramelo={() => invia({ tipo: 'mostramelo' })}
            testi={testi}
          />
        )}
      </div>
    </SopraLaBarra>
  );
}
